import type { ReferencedValue, ValueSignature, ValueTombstone } from "@skilitics/nautilus-core-designer-data";
import {
  isNodeValueReference,
  isValueTombstone,
  valueReference,
  valueSignature,
} from "@skilitics/nautilus-core-designer-data";

import { getExternalValueDescriptor } from "./externalValues.ts";
import type { GetNodeValue } from "./querying/getNodeValue.ts";
import type { ValueSubtypeSpec } from "./valueSubtypeSpecs.ts";

interface CreateOptions {
  getNodeValue: GetNodeValue;
}

/**
 * A signature as read off a live declaration, spec included. The stored
 * {@link ValueSignature} stays as it is — it is the tombstone's serialized shape
 * — so the spec joins it only here, where a descriptor is at hand to carry it.
 * A tombstoned subject therefore yields no spec: what was never stored cannot be
 * preserved, and an operand that no longer meets bounds nobody remembers reads
 * as valid rather than as an error the author cannot act on.
 */
export type ResolvedValueSignature = ValueSignature & { subtypeSpec?: ValueSubtypeSpec };

/**
 * The type, subtype and spec an expression derives from its subject. `undefined`
 * means the subject cannot be resolved — the expression is invalid and abstains.
 */
export type ResolveValueSignature = (subject: ReferencedValue | ValueTombstone) => ResolvedValueSignature | undefined;

/**
 * Read an expression subject's signature off whatever it points at.
 *
 * A tombstoned subject resolves fine: preserving the signature of the removed
 * value is the whole purpose of a tombstone, so the expression stays editable
 * and its form keeps rendering while the author repairs the binding. What does
 * not resolve is a live reference whose node is gone or no longer advertises the
 * value — the producer's veto — since nothing then says what the comparison is
 * over.
 */
export const resolveValueSignatureCreate = ({ getNodeValue }: CreateOptions): ResolveValueSignature => {
  return (subject) => {
    if (isValueTombstone(subject)) return valueSignature(subject);

    const reference = valueReference(subject);
    const descriptor = isNodeValueReference(reference)
      ? getNodeValue(reference)?.descriptor
      : getExternalValueDescriptor(reference.property);

    if (!descriptor) return undefined;
    return {
      type: descriptor.type,
      ...(descriptor.subtype && { subtype: descriptor.subtype }),
      ...(descriptor.subtypeSpec && { subtypeSpec: descriptor.subtypeSpec }),
    };
  };
};
